<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { WizardPage, WizardField, WizardFieldType, UpdateWizardFieldPayload } from '@/types/wizard-forms'
import { useWizardFields } from '@/composables/useWizardFields'
import { useWizardPages } from '@/composables/useWizardPages'
import WizardFieldGrid from './WizardFieldGrid.vue'
import WizardFieldToolbox from './WizardFieldToolbox.vue'
import WizardFieldEditor from './WizardFieldEditor.vue'

const props = defineProps<{
  page: WizardPage
  formId: number
}>()

const emit = defineEmits<{
  updated: []
  'field-added': []
  'field-updated': []
  'field-deleted': []
}>()

const { t } = useI18n()
const { fields, loading: fieldsLoading, error: fieldsError, fetchFields, createField, updateField } = useWizardFields()
const { updatePage } = useWizardPages()

// Inline page title editing
const isEditingTitle = ref(false)
const titleDraft = ref(props.page.title)
const titleSaving = ref(false)

watch(() => props.page.title, (v) => { titleDraft.value = v })

async function saveTitle(): Promise<void> {
  if (titleDraft.value.trim() === props.page.title) {
    isEditingTitle.value = false
    return
  }
  titleSaving.value = true
  try {
    await updatePage(props.formId, props.page.id, { title: titleDraft.value })
    emit('updated')
  } finally {
    titleSaving.value = false
    isEditingTitle.value = false
  }
}

function cancelTitleEdit(): void {
  titleDraft.value = props.page.title
  isEditingTitle.value = false
}

// Fields
const selectedField = ref<WizardField | null>(null)

async function loadFields(): Promise<void> {
  await fetchFields(props.formId, props.page.id)
}

watch(() => props.page.id, () => {
  selectedField.value = null
  void loadFields()
}, { immediate: true })

function handleSelectField(fieldId: number): void {
  const found = fields.value.find((f) => f.id === fieldId) ?? null
  selectedField.value = found
}

function buildFieldPayload(field: WizardField, overrides: Partial<{ grid_row: number; grid_column: number }> = {}): UpdateWizardFieldPayload {
  const payload: UpdateWizardFieldPayload = {
    type: field.type,
    label: field.label,
    placeholder: field.placeholder,
    help_text: field.help_text,
    is_required: field.is_required,
    grid_row: field.grid_row,
    grid_column: field.grid_column,
    sort_order: field.sort_order,
    extra_cost: field.extra_cost !== null && field.extra_cost !== undefined ? parseFloat(field.extra_cost) : null,
    ...overrides,
  }
  // Only include options when non-empty. The backend rule 'options' => ['min:1'] rejects
  // an empty array, and non-option fields receive options:[] from the API when eager-loaded.
  if (field.options && field.options.length > 0) {
    payload.options = field.options.map((o) => ({
      id: o.id,
      label: o.label,
      value: o.value,
      extra_cost: parseFloat(o.extra_cost),
      sort_order: o.sort_order,
    }))
  }
  return payload
}

async function handleMoveField(fieldId: number, gridRow: number, gridColumn: number): Promise<void> {
  const field = fields.value.find((f) => f.id === fieldId)
  if (!field) return
  await updateField(props.formId, props.page.id, fieldId, buildFieldPayload(field, { grid_row: gridRow, grid_column: gridColumn }))
  emit('field-updated')
}

async function handleInsertBeforeRow(fieldId: number, targetRow: number): Promise<void> {
  const field = fields.value.find((f) => f.id === fieldId)
  if (!field) return

  // Dropping on the insert zone directly above the field's own row is a no-op.
  // Redirect to the row above so the field actually moves somewhere visible.
  const effectiveRow = field.grid_row === targetRow && targetRow > 1 ? targetRow - 1 : targetRow

  // True no-op: field is already at effectiveRow col 1 with nothing to shift.
  if (field.grid_row === effectiveRow && field.grid_column === 1) return

  const fieldsToShift = fields.value.filter((f) => f.id !== fieldId && f.grid_row >= effectiveRow)
  await Promise.all([
    updateField(props.formId, props.page.id, fieldId, buildFieldPayload(field, { grid_row: effectiveRow, grid_column: 1 })),
    ...fieldsToShift.map((f) =>
      updateField(props.formId, props.page.id, f.id, buildFieldPayload(f, { grid_row: f.grid_row + 1 })),
    ),
  ])
  emit('field-updated')
}

async function handleAddField(type: WizardFieldType): Promise<void> {
  const OPTION_TYPES: WizardFieldType[] = ['checkbox_group', 'radio_group', 'select']
  const nextRow = fields.value.length > 0 ? Math.max(...fields.value.map((f) => f.grid_row)) + 1 : 1

  const payload = OPTION_TYPES.includes(type)
    ? {
        type,
        label: t(`wizard_forms.field_types.${type}`),
        grid_row: nextRow,
        grid_column: 1,
        options: [{ label: t('wizard_forms.options.default_label'), value: 'option_1', extra_cost: 0, sort_order: 0 }],
      }
    : {
        type,
        label: t(`wizard_forms.field_types.${type}`),
        grid_row: nextRow,
        grid_column: 1,
      }

  const created = await createField(props.formId, props.page.id, payload)
  selectedField.value = created
  emit('field-added')
}

function handleFieldSaved(): void {
  void loadFields()
  selectedField.value = null
  emit('field-updated')
}

function handleFieldDeleted(): void {
  void loadFields()
  selectedField.value = null
  emit('field-deleted')
}
</script>

<template>
  <div class="flex flex-col gap-4 h-full">
    <!-- Page title -->
    <div class="flex items-center gap-3">
      <template v-if="isEditingTitle">
        <input
          v-model="titleDraft"
          type="text"
          class="flex-1 rounded-lg bg-slate-700/50 border border-emerald-500 px-3 py-1.5 text-base font-semibold text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
          @keydown.enter="saveTitle"
          @keydown.escape="cancelTitleEdit"
        />
        <button
          type="button"
          :disabled="titleSaving"
          class="text-xs font-medium text-emerald-400 hover:text-emerald-300 disabled:opacity-50"
          @click="saveTitle"
        >
          {{ titleSaving ? t('common.loading') : t('common.save') }}
        </button>
        <button
          type="button"
          class="text-xs text-slate-400 hover:text-white"
          @click="cancelTitleEdit"
        >
          {{ t('common.cancel') }}
        </button>
      </template>
      <template v-else>
        <h2 class="text-base font-semibold text-white flex-1">{{ props.page.title }}</h2>
        <button
          type="button"
          class="text-xs text-slate-400 hover:text-white border border-slate-600 hover:border-slate-500 rounded px-2 py-1 transition-colors"
          @click="isEditingTitle = true"
        >
          {{ t('wizard_forms.page_editor.rename') }}
        </button>
      </template>
    </div>

    <!-- Error state -->
    <div
      v-if="fieldsError"
      class="rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
    >
      {{ t(fieldsError) }}
    </div>

    <!-- Loading state -->
    <div v-if="fieldsLoading" class="flex items-center justify-center py-10">
      <svg class="animate-spin h-7 w-7 text-emerald-500" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    </div>

    <template v-else>
      <div class="flex gap-4 flex-1 min-h-0">
        <!-- Grid preview -->
        <div class="flex-1 min-w-0">
          <WizardFieldGrid
            :fields="fields"
            @select="handleSelectField"
            @move="handleMoveField"
            @insert-before="handleInsertBeforeRow"
          />
        </div>

        <!-- Field editor panel or toolbox -->
        <div class="w-64 shrink-0">
          <WizardFieldEditor
            v-if="selectedField"
            :field="selectedField"
            :form-id="props.formId"
            :page-id="props.page.id"
            @saved="handleFieldSaved"
            @deleted="handleFieldDeleted"
            @cancel="selectedField = null"
          />
          <WizardFieldToolbox
            v-else
            @add="handleAddField"
          />
        </div>
      </div>
    </template>
  </div>
</template>
